/**
 * ChangeTypeModal — Modal para cambiar el tipo base de un Trabajo.
 *
 * Extraído de InternalWorkspace.jsx en el refactor 2026-02-08 (cero
 * cambios funcionales: mismas props, mismos data-testids, misma lógica
 * de premium/locked/quota guards).
 *
 * Reglas oficiales (Cuadro Negro):
 * - Free: 1 cambio total / 2 trabajos por mes; tras cambiar queda
 *   `type_locked`.
 * - Premium: ilimitado.
 */
import React, { useState, useEffect } from 'react';
import { X, RefreshCw, Lock, AlertTriangle, Loader2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { CANONICAL_BASE_TYPES } from './CreateModuleModal';

export const ChangeTypeModal = ({ isOpen, module, quotaStatus, onClose, onConfirm }) => {
  const { language } = useLanguage();
  const [newType, setNewType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && module) {
      setNewType(module.base_type || '');
    }
  }, [isOpen, module]);

  if (!isOpen || !module) return null;

  const isPremium = quotaStatus?.is_premium;
  const isLocked = !!module.type_locked;
  const canChange = isPremium || (!isLocked && quotaStatus?.can_change_type);

  const t = language === 'es' ? {
    title: 'Cambiar Tipo Base',
    currentType: 'Tipo actual',
    newType: 'Nuevo tipo',
    selectPlaceholder: 'Selecciona un tipo...',
    warningTitle: 'Atención',
    warningFree: (
      'Cambiar el tipo creará un Trabajo nuevo y descartará el código actual. ' +
      'Este cambio CONSUME 1 Trabajo de tu cuota mensual y fijará el tipo permanentemente. ' +
      'Sólo Premium puede modificar tipos sin restricciones.'
    ),
    lockedTitle: 'Tipo fijado',
    lockedMsg: 'Este Trabajo ya fijó su tipo base y no puede cambiarse en el Plan Gratis. Actualiza a Premium para tipos ilimitados.',
    quotaExceeded: 'Has alcanzado el límite de cambios de tipo / Trabajos del mes.',
    cancel: 'Cancelar',
    confirm: 'Confirmar Cambio',
    upgrade: 'Actualizar a Premium',
    submitting: 'Cambiando...'
  } : {
    title: 'Change Base Type',
    currentType: 'Current type',
    newType: 'New type',
    selectPlaceholder: 'Select a type...',
    warningTitle: 'Warning',
    warningFree: (
      'Changing the type will create a new Work and discard the current code. ' +
      'This change CONSUMES 1 Work of your monthly quota and will lock the type permanently. ' +
      'Only Premium can modify types without restrictions.'
    ),
    lockedTitle: 'Type locked',
    lockedMsg: 'This Work has already locked its base type and cannot be changed on the Free Plan. Upgrade to Premium for unlimited types.',
    quotaExceeded: 'You have reached the limit of type changes / Works this month.',
    cancel: 'Cancel',
    confirm: 'Confirm Change',
    upgrade: 'Upgrade to Premium',
    submitting: 'Changing...'
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!newType || newType === module.base_type) return;
    setIsSubmitting(true);
    try {
      await onConfirm(newType);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h2 className="text-base font-semibold text-white flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-amber-400" />
            {t.title}
          </h2>
          <button
            onClick={onClose}
            data-testid="change-type-modal-close-btn"
            className="p-1 hover:bg-slate-700 rounded-lg"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {/* Bloqueo: Free + ya cambiado una vez. */}
          {isLocked && !isPremium ? (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
              <Lock className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-red-300">{t.lockedTitle}</h3>
                <p className="text-xs text-slate-300 mt-1">{t.lockedMsg}</p>
              </div>
            </div>
          ) : !isPremium && quotaStatus && !quotaStatus.can_change_type ? (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{t.quotaExceeded}</p>
            </div>
          ) : !isPremium ? (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-amber-300">{t.warningTitle}</h3>
                <p className="text-xs text-slate-300 mt-1">{t.warningFree}</p>
              </div>
            </div>
          ) : null}

          <div>
            <label className="block text-xs text-slate-400 mb-1">{t.currentType}</label>
            <div className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white">
              {module.base_type || '—'}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <label className="block text-xs text-slate-400 mb-1">{t.newType}</label>
            <select
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              disabled={!canChange}
              data-testid="change-type-modal-select"
              className="w-full bg-slate-900 text-white border border-slate-600 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none disabled:opacity-50"
            >
              <option value="">{t.selectPlaceholder}</option>
              {CANONICAL_BASE_TYPES.map((bt) => (
                <option key={bt} value={bt}>{bt}</option>
              ))}
            </select>
          </form>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-700">
          <button
            onClick={onClose}
            data-testid="change-type-modal-cancel-btn"
            className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-md hover:bg-slate-600"
          >
            {t.cancel}
          </button>
          {canChange ? (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !newType || newType === module.base_type}
              data-testid="change-type-modal-confirm-btn"
              className="px-3 py-1.5 text-sm bg-amber-500 text-black font-medium rounded-md hover:bg-amber-400 disabled:opacity-50"
            >
              {isSubmitting ? t.submitting : t.confirm}
            </button>
          ) : (
            <a
              href="/precios"
              data-testid="change-type-modal-upgrade-btn"
              className="px-3 py-1.5 text-sm bg-gradient-to-r from-purple-500 to-blue-500 text-white font-medium rounded-md hover:opacity-90"
            >
              {t.upgrade}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangeTypeModal;
